import { Button, Checkbox, Label } from "flowbite-react";
import { ArrowRight } from "lucide-react";
import { LEGAL_COMPLIANCE_COPY } from "@/features/legal/config/legal.config";
import { ButtonWithLoader } from "@/shared/components/customButtons/ButtonWithSpinner";

const ACCEPTANCE_CHECKBOX_ID = "legal-compliance-acceptance-checkbox";

type LegalComplianceActionBarProps = {
  actionLabel: string
  canAccept: boolean
  isChecked: boolean
  onCheckedChange: (checked: boolean) => void
  notice: string | null
  isPresentationOutdated: boolean
  isAccepting: boolean
  onAccept: () => void
  onRefuse: () => void
};

type AcceptActionProps = Pick<LegalComplianceActionBarProps, "isPresentationOutdated" | "isAccepting" | "isChecked" | "onAccept">;

const AcceptAction = ({
  isPresentationOutdated, isAccepting, isChecked, onAccept,
}: AcceptActionProps) => {
  if (isPresentationOutdated) {
    return (
      <Button pill color="primary" onClick={() => window.location.reload()} data-testid="legal-compliance-reload-button">
        {LEGAL_COMPLIANCE_COPY.RELOAD_PAGE}
      </Button>
    );
  }

  return (
    <ButtonWithLoader
      pill
      color="primary"
      isLoading={isAccepting}
      disabled={isAccepting || !isChecked}
      onClick={onAccept}
      data-testid="legal-compliance-accept-button"
    >
      <span className="flex items-center gap-2">
        {LEGAL_COMPLIANCE_COPY.ACCEPT}
        <ArrowRight size={16} aria-hidden />
      </span>
    </ButtonWithLoader>
  );
};

// The checkbox carries the exact label the API records in the proof; it
// starts unchecked and gates the accept button. An impersonated session may
// read the terms but never gets the checkbox.
export const LegalComplianceActionBar = ({
  actionLabel, canAccept, isChecked, onCheckedChange, notice, isPresentationOutdated, isAccepting, onAccept, onRefuse,
}: LegalComplianceActionBarProps) => (
  <div className="space-y-5">
    {canAccept
      ? (
        <div className="flex items-start gap-3">
          <Checkbox
            id={ACCEPTANCE_CHECKBOX_ID}
            className="mt-1 size-5 shrink-0 rounded-full"
            checked={isChecked}
            disabled={isAccepting || isPresentationOutdated}
            onChange={(event) => onCheckedChange(event.target.checked)}
            data-testid="legal-compliance-acceptance-checkbox"
          />
          <Label htmlFor={ACCEPTANCE_CHECKBOX_ID} className="text-base leading-7 text-gray-700 dark:text-gray-200">
            {actionLabel}
          </Label>
        </div>
      )
      : <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="legal-compliance-cannot-accept">{LEGAL_COMPLIANCE_COPY.CANNOT_ACCEPT}</p>}
    <p role="alert" className="text-sm text-error empty:hidden">
      {notice}
    </p>
    <div className="flex flex-wrap items-center justify-between gap-4">
      <button
        type="button"
        disabled={isAccepting}
        onClick={onRefuse}
        className="text-sm text-gray-500 hover:text-primary disabled:opacity-60 dark:text-gray-400"
        data-testid="legal-compliance-refuse-button"
      >
        {canAccept ? LEGAL_COMPLIANCE_COPY.REFUSE : LEGAL_COMPLIANCE_COPY.LOGOUT}
      </button>
      {canAccept && (
        <AcceptAction
          isPresentationOutdated={isPresentationOutdated}
          isAccepting={isAccepting}
          isChecked={isChecked}
          onAccept={onAccept}
        />
      )}
    </div>
  </div>
);
